import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { ObjectFitModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import {
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { OBJECT_FIT_OPTIONS } from "@v2/features/modifiers/constants";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

interface ObjectFitModifierProps {
  onChange: (modifier: ObjectFitModifierValue) => void;
}

const VALID_OBJECT_FIT_VALUES: Set<string> = new Set(
  OBJECT_FIT_OPTIONS.map((option) => option.value),
);

const ObjectFitModifier: React.FC<ObjectFitModifierProps> = ({ onChange }) => {
  const objectFitFromElement =
    useSemanticModifierValue({
      semanticExtractor: ({ semanticElement }) => {
        const className = semanticElement.cssClasses;
        if (!className) {
          return null;
        }
        for (const cssClass of className.split(" ")) {
          if (cssClass.startsWith("object-")) {
            const objectFitValue = cssClass.slice(7);
            if (VALID_OBJECT_FIT_VALUES.has(objectFitValue)) {
              return objectFitValue as ObjectFitModifierValue["value"];
            }
          }
        }
        return null;
      },
    }) ?? "cover";

  const [objectFit, setObjectFit] = useOverridableState(
    objectFitFromElement,
    React.useCallback(
      (value: ObjectFitModifierValue["value"]) => {
        onChange({ type: "objectFit", value });
      },
      [onChange],
    ),
  );

  const handleObjectFitChange = React.useCallback(
    (value: string | null) => {
      if (value && VALID_OBJECT_FIT_VALUES.has(value)) {
        setObjectFit(value as ObjectFitModifierValue["value"]);
      }
    },
    [setObjectFit],
  );

  const items: ComboboxItemWithValue[] = OBJECT_FIT_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const selectedItem = items.find((item) => item.value === objectFit) ?? null;

  return (
    <div className="object-fit-modifier">
      <ComboboxRoot
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          handleObjectFitChange(item?.value ?? null);
        }}
        itemToStringValue={(item) => String(item.label)}
        isItemEqualToValue={(a, b) => a.value === b.value}
        filter={null}
      >
        <ComboboxTriggerButton className="w-full">
          {selectedItem?.label ?? "Cover"}
        </ComboboxTriggerButton>
        <ComboboxContent
          sideOffset={8}
          side="right"
          align="center"
          UNSAFE_className="w-[160px]"
        >
          <ComboboxList>
            {(item: ComboboxItemWithValue) => (
              <ComboboxItem key={item.value} {...item} value={item} />
            )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
    </div>
  );
};

export default ObjectFitModifier;
